import React, { useState, useEffect } from 'react';
import { userApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ExamHistoryList from '../components/ExamHistoryList';
import PaidPlanSelector from '../components/PaidPlanSelector';
import AchievementSection from '../components/AchievementSection';
import './ProfilePage.css';

interface ProfilePageProps {
  onBack: () => void;
  onViewResults?: (sessionId: string) => void;
}

interface UserProfile {
  user_id: string;
  email: string;
  name: string;
  profile_picture?: string;
  total_exams_taken: number;
  average_score: number;
  created_at: string;
  last_login: string;
}

interface UserStatistics {
  totalExams: number;
  averageScore: number;
  bestScore: number;
  recentTrend: string;
  examTypeStats: any[];
  monthlyProgress: any[];
}

interface ExamHistory {
  sessionId: string;
  examType: string;
  score: number;
  percentage: number;
  timeTakenMinutes: number;
  completedAt: string;
  passed: boolean;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ onBack, onViewResults }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [recentHistory, setRecentHistory] = useState<ExamHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', profile_picture: '' });
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);



  useEffect(() => {

    const loadProfile = async () => {
      if (!user?.user_id) {

        setIsLoading(false);
        return;
      }


      try {
        // 프로필 정보 로드
        const profileResponse = await userApi.getProfile();
        if (profileResponse.success && profileResponse.data) {
          setProfile(profileResponse.data as UserProfile);
          setEditForm({
            name: (profileResponse.data as any).name || user.name || '',
            profile_picture: (profileResponse.data as any).profile_picture || user.profile_picture || ''
          });
        } else {
          // 프로필이 없으면 사용자 정보로 기본 프로필 생성
          const defaultProfile: UserProfile = {
            user_id: user.user_id,
            email: user.email || '',
            name: user.name || '',
            profile_picture: user.profile_picture,
            total_exams_taken: 0,
            average_score: 0,
            created_at: new Date().toISOString(),
            last_login: new Date().toISOString()
          };
          setProfile(defaultProfile);
          setEditForm({
            name: user.name || '',
            profile_picture: user.profile_picture || ''
          });
        }
        
        // 통계 정보 로드
        try {
          const statsResponse = await userApi.getStatistics();
          if (statsResponse.success) {
            setStatistics(statsResponse.data as UserStatistics);
          }
        } catch (statsErr) {

        }

        // 최근 시험 기록 로드 (최근 3개)
        try {
          const historyResponse = await userApi.getHistory({ limit: 10 });
          if (historyResponse.success && historyResponse.data) {
            const history = (historyResponse.data as any)?.history || [];
            setRecentHistory(history.slice(0, 3));
          }
        } catch (historyErr) {

          setRecentHistory([]);
        }
        
      } catch (err) {

        setError('프로필을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user?.user_id, user?.email, user?.name, user?.profile_picture]);

  const handleSave = async () => {
    if (!user?.user_id || !profile) return;

    try {
      const response = await userApi.updateProfile(editForm);
      if (response.success) {
        setProfile(response.data as UserProfile);
        setIsEditing(false);
        setSaveMessage('프로필이 성공적으로 업데이트되었습니다.');
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setError('프로필 업데이트에 실패했습니다.');
      }
    } catch (err) {
      setError('프로필 업데이트 중 오류가 발생했습니다.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="profile-page">
        <div className="loading">프로필을 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <div className="profile-header">
          <button className="back-button" onClick={onBack}>← 돌아가기</button>
          <h1>내 정보</h1>
        </div>
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-page">
        <div className="profile-header">
          <button className="back-button" onClick={onBack}>← 돌아가기</button>
          <h1>내 정보</h1>
        </div>
        <div className="loading">프로필을 준비하는 중...</div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <button className="back-button" onClick={onBack}>← 돌아가기</button>
        <h1>내 정보</h1>
      </div>

      {saveMessage && (
        <div className="save-message">{saveMessage}</div>
      )}

      <div className="profile-content">
        <div className="profile-card">
          <div className="profile-avatar">
            {profile.profile_picture ? (
              <img src={profile.profile_picture} alt="프로필" />
            ) : (
              <div className="avatar-placeholder">
                {profile.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          <div className="profile-info">
            {isEditing ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>이름</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="이름을 입력하세요"
                  />
                </div>
                <div className="form-group">
                  <label>프로필 이미지 URL</label>
                  <input
                    type="url"
                    value={editForm.profile_picture}
                    onChange={(e) => setEditForm({ ...editForm, profile_picture: e.target.value })}
                    placeholder="이미지 URL을 입력하세요"
                  />
                </div>
                <div className="form-actions">
                  <button className="save-button" onClick={handleSave}>저장</button>
                  <button className="cancel-button" onClick={() => setIsEditing(false)}>취소</button>
                </div>
              </div>
            ) : (
              <div className="view-mode">
                <h2>{profile.name}</h2>
                <p className="email">{profile.email}</p>
                <button className="edit-button" onClick={() => setIsEditing(true)}>
                  정보 수정
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="stats-section">
          <h3>시험 통계</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">{statistics?.totalExams || profile.total_exams_taken}</div>
              <div className="stat-label">총 시험 횟수</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{statistics?.averageScore || profile.average_score}%</div>
              <div className="stat-label">평균 점수</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{statistics?.bestScore || 0}%</div>
              <div className="stat-label">최고 점수</div>
            </div>
          </div>
        </div>

        <AchievementSection />

        <div className="recent-exams-section">
          <h3>최근 시험 기록</h3>
          <ExamHistoryList 
            history={recentHistory} 
            onViewDetails={(sessionId) => {
              onViewResults?.(sessionId);
            }}
          />
        </div>

        <PaidPlanSelector currentPlan={user?.paid_plan ?? 0} />

        <div className="coupon-section">
          <h3>쿠폰 등록</h3>
          <div className="coupon-form">
            <input
              type="text"
              placeholder="쿠폰 코드를 입력하세요"
              onKeyPress={async (e) => {
                if (e.key === 'Enter') {
                  const couponCode = e.currentTarget.value.trim();
                  if (!couponCode) return;
                  
                  try {
                    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/coupon/use`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                      },
                      body: JSON.stringify({ couponCode })
                    });
                    
                    if (response.ok) {
                      alert('쿠폰이 성공적으로 등록되었습니다!');
                      e.currentTarget.value = '';
                    } else {
                      const error = await response.json();
                      alert(error.error?.message || error.message || '쿠폰 등록에 실패했습니다.');
                    }
                  } catch (error) {
                    alert('쿠폰 등록 중 오류가 발생했습니다.');
                  }
                }
              }}
            />
            <button
              onClick={async (e) => {
                const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                const couponCode = input.value.trim();
                if (!couponCode) return;
                
                try {
                  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/coupon/use`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    },
                    body: JSON.stringify({ couponCode })
                  });
                  
                  if (response.ok) {
                    alert('쿠폰이 성공적으로 등록되었습니다!');
                    input.value = '';
                  } else {
                    const error = await response.json();
                    alert(error.error?.message || error.message || '쿠폰 등록에 실패했습니다.');
                  }
                } catch (error) {
                  alert('쿠폰 등록 중 오류가 발생했습니다.');
                }
              }}
            >
              등록
            </button>
          </div>
        </div>

        <div className="account-info">
          <h3>계정 정보</h3>
          <div className="info-item">
            <span className="info-label">가입일:</span>
            <span className="info-value">{formatDate(profile.created_at)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">마지막 로그인:</span>
            <span className="info-value">{formatDate(profile.last_login)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;